import { imageUrlToFile } from "./share";

export type ShareFormat = "square" | "story";

export interface ComposeOptions {
  faceUrl: string;
  selfieUrl?: string | null;
  format: ShareFormat;
  showOriginal: boolean;
}

const DIMS: Record<ShareFormat, { w: number; h: number }> = {
  square: { w: 1080, h: 1080 },
  story: { w: 1080, h: 1920 },
};

const BG = "#0a0d0e";
const MINT = "#2fd6a6";
const FONT = '"Pretendard Variable", Pretendard, system-ui, sans-serif';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("share image load failed"));
    img.src = src;
  });
}

/** object-fit: cover — draw img filling the rect, cropping the overflow. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const imgRatio = img.width / img.height;
  const boxRatio = w / h;
  let sx = 0;
  let sy = 0;
  let sw = img.width;
  let sh = img.height;
  if (imgRatio > boxRatio) {
    sw = img.height * boxRatio;
    sx = (img.width - sw) / 2;
  } else {
    sh = img.width / boxRatio;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function chip(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  fontSize: number,
): void {
  ctx.font = `700 ${fontSize}px ${FONT}`;
  const padX = fontSize * 0.7;
  const w = ctx.measureText(text).width + padX * 2;
  const h = fontSize * 1.9;
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, h / 2);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + padX, y + h / 2 + 1);
}

function imageCard(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
  label: string | null,
): void {
  const r = 40;
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.clip();
  drawCover(ctx, img, x, y, w, h);
  ctx.restore();
  ctx.lineWidth = 2;
  ctx.strokeStyle = "rgba(255,255,255,0.10)";
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.stroke();
  if (label) chip(ctx, label, x + 20, y + 20, 26);
}

/**
 * Render the chosen share template (branded, optional before/after) to a File
 * suitable for the native share sheet or download. Falls back to null if the
 * source image cannot be read (cross-origin taint).
 */
export async function composeShareImage(options: ComposeOptions): Promise<File | null> {
  const { faceUrl, selfieUrl, format, showOriginal } = options;

  // Read through a blob object URL so a cross-origin image never taints canvas.
  const faceFile = await imageUrlToFile(faceUrl);
  const faceSrc = faceFile ? URL.createObjectURL(faceFile) : faceUrl;

  try {
    if (typeof document.fonts?.ready?.then === "function") {
      await document.fonts.ready;
    }
    const face = await loadImage(faceSrc);
    const original = showOriginal && selfieUrl ? await loadImage(selfieUrl) : null;

    const { w, h } = DIMS[format];
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    // background + mint glow
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, w, h);
    const glow = ctx.createRadialGradient(w * 0.85, h * 0.08, 0, w * 0.85, h * 0.08, w * 0.7);
    glow.addColorStop(0, "rgba(47,214,166,0.18)");
    glow.addColorStop(1, "rgba(47,214,166,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);

    const pad = format === "story" ? 72 : 64;

    // title
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    const eyebrowY = pad + 42;
    ctx.font = `700 30px ${FONT}`;
    ctx.fillStyle = MINT;
    ctx.fillText("🎲 10억 얼굴 룰렛", w / 2, eyebrowY);

    const titleSize = format === "story" ? 78 : 64;
    ctx.font = `800 ${titleSize}px ${FONT}`;
    ctx.fillStyle = "#f1f4f3";
    const line1Y = eyebrowY + titleSize + 18;
    ctx.fillText("10억 받고 얼굴 랜덤", w / 2, line1Y);
    ctx.fillStyle = MINT;
    ctx.fillText("돌리기", w / 2, line1Y + titleSize + 8);

    // footer
    const footerY = h - pad - 6;
    ctx.font = `700 34px ${FONT}`;
    ctx.fillStyle = MINT;
    ctx.fillText("randomface.win", w / 2, footerY);

    // image region between title and footer
    const top = line1Y + titleSize + 8 + 40;
    const bottom = footerY - 54;
    const regionX = pad;
    const regionW = w - pad * 2;
    const regionH = bottom - top;

    if (!original) {
      imageCard(ctx, face, regionX, top, regionW, regionH, null);
    } else if (format === "story") {
      const gap = 24;
      const cardH = (regionH - gap) / 2;
      imageCard(ctx, original, regionX, top, regionW, cardH, "원본");
      imageCard(ctx, face, regionX, top + cardH + gap, regionW, cardH, "새 얼굴");
    } else {
      const gap = 20;
      const cardW = (regionW - gap) / 2;
      imageCard(ctx, original, regionX, top, cardW, regionH, "원본");
      imageCard(ctx, face, regionX + cardW + gap, top, cardW, regionH, "새 얼굴");
    }

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
    });
    if (!blob) return null;
    return new File([blob], `randomface-${format}-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });
  } catch {
    return null;
  } finally {
    if (faceFile) URL.revokeObjectURL(faceSrc);
  }
}
